// chess.js wrapper — single source of truth for game rules
import { Chess, type Square, type Move } from 'chess.js';

export type GameResult = 'ongoing' | 'checkmate' | 'stalemate' | 'draw_repetition' | 'insufficient_material' | 'fifty_move_rule';

export class ChessGame {
  private game: Chess;

  constructor(fen?: string) {
    this.game = new Chess(fen);
  }

  /** Current FEN position */
  fen(): string {
    return this.game.fen();
  }

  /** Make a move. Returns the move object or null if illegal */
  move(from: Square, to: Square, promotion?: string): Move | null {
    try {
      return this.game.move({ from, to, promotion });
    } catch {
      return null;
    }
  }

  /** Get all legal moves for a square */
  legalMoves(square: Square): Move[] {
    return this.game.moves({ square, verbose: true });
  }

  /** Get all legal moves for current position */
  allLegalMoves(): Move[] {
    return this.game.moves({ verbose: true });
  }

  /** Is it check? */
  isCheck(): boolean {
    return this.game.isCheck();
  }

  /** Is it checkmate? */
  isCheckmate(): boolean {
    return this.game.isCheckmate();
  }

  /** Is it stalemate? */
  isStalemate(): boolean {
    return this.game.isStalemate();
  }

  /** Is it a draw? (any reason) */
  isDraw(): boolean {
    return this.game.isDraw();
  }

  /** Is it insufficient material? */
  isInsufficientMaterial(): boolean {
    return this.game.isInsufficientMaterial();
  }

  /** Is it draw by repetition? */
  isThreefoldRepetition(): boolean {
    return this.game.isThreefoldRepetition();
  }

  /** Current turn: 'w' or 'b' */
  turn(): 'w' | 'b' {
    return this.game.turn();
  }

  /** Get the game result */
  result(): GameResult {
    if (this.game.isCheckmate()) return 'checkmate';
    if (this.game.isStalemate()) return 'stalemate';
    if (this.game.isThreefoldRepetition()) return 'draw_repetition';
    if (this.game.isInsufficientMaterial()) return 'insufficient_material';
    if (this.game.isDraw()) return 'fifty_move_rule';
    return 'ongoing';
  }

  /** Undo last move */
  undo(): Move | null {
    return this.game.undo();
  }

  /** Reset to starting position */
  reset(): void {
    this.game.reset();
  }

  /** Get move history */
  history(): string[] {
    return this.game.history();
  }

  /** Get verbose move history with from/to */
  historyVerbose(): { from: string; to: string; san: string; captured?: string; promotion?: string }[] {
    return this.game.history({ verbose: true }) as any;
  }

  /** Get PGN */
  pgn(): string {
    return this.game.pgn();
  }

  /** Load from FEN */
  loadFen(fen: string): boolean {
    try {
      this.game.load(fen);
      return true;
    } catch {
      return false;
    }
  }

  /** Board as 2D array */
  board(): ReturnType<Chess['board']> {
    return this.game.board();
  }

  /** Number of half-moves */
  moveNumber(): number {
    return this.game.moveNumber();
  }
}
